import { formatDateOnlyDMY } from "helpers";
import { ArticleLocation, TransferOriginRow } from "types";
import { ArticleLocationRow, InventoryStatus } from "./types";

export const toNumber = (value: unknown) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const buildArticleLocationRows = (
  articleLocations: ArticleLocation[],
): ArticleLocationRow[] =>
  articleLocations.flatMap((articleLocation) => {
    const totalStock = Math.max(0, toNumber(articleLocation.stock));
    const reserved = Math.max(0, toNumber(articleLocation.reserved));
    const unavailable = Math.max(
      0,
      toNumber(articleLocation.not_available),
    );
    const inWorkOrders = Math.max(0, toNumber(articleLocation.in_work_orders));
    const available = Math.max(0, totalStock - reserved - unavailable - inWorkOrders);
    const rows: ArticleLocationRow[] = [];

    if (
      available > 0 ||
      reserved > 0 ||
      (totalStock > inWorkOrders && unavailable === 0)
    ) {
      rows.push({
        ...articleLocation,
        row_key: `${articleLocation.id}-available`,
        stock: available,
        reserved,
        not_available: 0,
        quantity: available,
        reserved_quantity: reserved,
        status: "available",
        status_label: "Disponible",
      });
    }

    if (unavailable > 0) {
      rows.push({
        ...articleLocation,
        row_key: `${articleLocation.id}-unavailable`,
        stock: unavailable,
        reserved: 0,
        not_available: unavailable,
        quantity: unavailable,
        reserved_quantity: null,
        status: "unavailable",
        status_label: "No disponible",
      });
    }

    return rows;
  });

export const buildTransferOriginRows = (
  transferOriginRows: TransferOriginRow[],
): ArticleLocationRow[] =>
  transferOriginRows.map((originRow) => {
    const baseArticleLocation = originRow.article_location;
    const reservedQuantity =
      originRow.origin_kind === "reserved"
        ? originRow.quantity
        : originRow.origin_kind === "available"
          ? originRow.reserved_total
          : null;

    return {
      ...baseArticleLocation,
      id: originRow.article_location_id,
      income_id: originRow.income_id,
      row_key: originRow.row_key,
      origin_kind: originRow.origin_kind,
      reserved_movement_id: originRow.reserved_movement_id,
      associated_client: originRow.associated_client,
      stock: originRow.quantity,
      reserved:
        originRow.origin_kind === "reserved"
          ? originRow.quantity
          : originRow.reserved_total,
      not_available:
        originRow.origin_kind === "unavailable" ? originRow.quantity : 0,
      quantity: originRow.quantity,
      reserved_quantity: reservedQuantity,
      status: originRow.origin_kind,
      status_label: originRow.status_label,
    };
  });

export const formatTransferCellValue = (
  value: unknown,
  fallback = "-",
): string | number => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return typeof value === "number" || typeof value === "string"
    ? value
    : String(value);
};

const isUnavailableSelection = (articleLocation?: ArticleLocation | null) =>
  toNumber(articleLocation?.not_available) > 0;

export const getArticleLocationSelectionStatus = (
  articleLocation?: ArticleLocation | null,
): InventoryStatus => {
  if (articleLocation?.origin_kind === "reserved") {
    return "reserved";
  }

  return isUnavailableSelection(articleLocation) ? "unavailable" : "available";
};

export const mapRowToSelectedArticleLocation = (
  row: ArticleLocationRow,
): ArticleLocation => ({
  id: row.id,
  row_key: row.row_key,
  income_id: row.income_id ?? null,
  article_id: row.article_id,
  location_id: row.location_id,
  article: row.article,
  location: row.location,
  stock: row.quantity,
  reserved:
    row.status === "reserved"
      ? row.quantity
      : row.status === "available"
        ? toNumber(row.reserved_quantity)
        : 0,
  not_available: row.status === "unavailable" ? row.quantity : 0,
  origin_kind: row.origin_kind ?? row.status,
  reserved_movement_id: row.reserved_movement_id ?? null,
  associated_client: row.associated_client ?? null,
  quantity: row.quantity,
  reserved_quantity: row.reserved_quantity,
  status_label: row.status_label,
  batch: row.batch,
  fecha_ingreso: row.fecha_ingreso,
  created_at: row.created_at,
  updated_at: row.updated_at,
  amount_per_container: row.amount_per_container,
  lote: row.lote,
  fecha_vencimiento: row.fecha_vencimiento,
  oc: row.oc,
  observation: row.observation,
});

export const renderTransferCellById = (
  id: keyof ArticleLocationRow,
  articleLocation: ArticleLocationRow,
) => {
  switch (id) {
    case "location_id": {
      if (!articleLocation.location) {
        return formatTransferCellValue(articleLocation.location_id);
      }

      const code = articleLocation.location.code ?? "";
      const storeDescription =
        articleLocation.location.store?.description ?? "";
      const storeCode = articleLocation.location.store?.code ?? "";
      const suffix =
        storeDescription || storeCode
          ? ` (${storeDescription} | ${storeCode})`
          : "";

      return formatTransferCellValue(
        `${code}${suffix}` || articleLocation.location_id,
      );
    }
    case "article_id": {
      if (!articleLocation.article) {
        return formatTransferCellValue(articleLocation.article_id);
      }

      const code = articleLocation.article.code ?? "";
      const description = articleLocation.article.description ?? "";

      return formatTransferCellValue(
        code && description
          ? `${code} - ${description}`
          : code || description || articleLocation.article_id,
      );
    }
    case "amount_per_container":
      return formatTransferCellValue(articleLocation.amount_per_container);
    case "fecha_ingreso":
      return formatTransferCellValue(
        articleLocation.fecha_ingreso
          ? formatDateOnlyDMY(articleLocation.fecha_ingreso)
          : "",
      );
    case "lote":
      return formatTransferCellValue(articleLocation.lote);
    case "oc":
      return formatTransferCellValue(articleLocation.oc);
    case "fecha_vencimiento":
      return formatTransferCellValue(
        articleLocation.fecha_vencimiento
          ? formatDateOnlyDMY(articleLocation.fecha_vencimiento)
          : "",
      );
    case "quantity":
      return articleLocation.quantity;
    case "reserved_quantity":
      return formatTransferCellValue(articleLocation.reserved_quantity);
    case "status_label":
      if (
        articleLocation.status === "reserved" &&
        articleLocation.reserved_movement_id
      ) {
        const suffix = articleLocation.associated_client
          ? ` - ${articleLocation.associated_client}`
          : "";

        return `Reservado (Pedido #${articleLocation.reserved_movement_id}${suffix})`;
      }

      return articleLocation.status_label;
    default:
      return null;
  }
};

export const buildTransferSearchKey = (
  articleLocation: ArticleLocationRow,
) => {
  const locationText = `${articleLocation.location?.code ?? ""} (${articleLocation.location?.store?.description ?? ""} | ${articleLocation.location?.store?.code ?? ""})`;
  const articleText = `${articleLocation.article?.code ?? ""} - ${articleLocation.article?.description ?? ""}`;
  const statusText = articleLocation.status_label.toLowerCase();

  return `${locationText} ${articleText} ${statusText}`.toLowerCase();
};
